import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { TranslatePipe, TranslateService, _ } from '@ngx-translate/core';
import FileSaver from 'file-saver';
import { combineLatest } from 'rxjs';
import { NotificationCount, NotificationKind } from 'src/app/models/notification';
import { PaginatedRequest } from 'src/app/models/paginated-request';
import { HelpSettings, ReleaseNotesSettings, TermsSettings, TopNavbarSettings } from 'src/app/models/preference-settings';
import { AuthStoreAction, AuthStoreSelectors, RootStoreState } from 'src/app/root-store';
import { CoreUiInterfaceStoreSelectors } from 'src/app/root-store/core-ui-interface.store';
import { ErrorHandlingStoreAction, ErrorHandlingStoreSelectors } from 'src/app/root-store/error-handling-store';
import { NotificationsStoreAction, NotificationsStoreSelectors } from 'src/app/root-store/notifications-store';
import { PreferencesSelectors } from 'src/app/root-store/preferences-store';
import { AuthService } from 'src/app/services/auth/auth.service';
import { HelpAuthService } from 'src/app/services/auth/help-auth.service';
import { CoreDataService } from 'src/app/services/core-data.service';
import { MessageNotifierService } from 'src/app/services/utils/message-notifier.service';
import { BaseComponent } from 'src/app/shared/base-components/base-component';
import { GlobalDateGridFilterPanelComponent } from 'src/app/shared/global-date-grid-filter-panel/global-date-grid-filter-panel.component';
import { environment } from 'src/environments/environment';
import { AuthUser } from '../../auth/auth-user';
import { ReleaseNotesIntroModalComponent } from '../../modal/news-modal/release-notes-intro-modal.component';
import { ReleaseNotesModalComponent, ReleaseNotesModalData } from '../../modal/news-modal/release-notes-modal.component';
import { TermsModalComponent, TermsModalData } from '../../modal/terms-modal/terms-modal.component';
import { ViewasuserModalDialogComponent } from '../../modal/viewasuser-modal-dialog/viewasuser-modal-dialog.component';
import { PreloadImageComponent } from '../../preload-image/preload-image.component';
import { TaricsSendEntitlementsComponent } from '../../tarics-send-entitlements/tarics-send-entitlements.component';
import { GlobalSearchBarComponent } from '../global-search-bar/global-search-bar.component';

@Component({
  selector: 'app-top-navbar-content',
  templateUrl: './top-navbar-content.component.html',
  styleUrls: ['./top-navbar-content.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    RouterModule,
    GlobalSearchBarComponent,
    GlobalDateGridFilterPanelComponent,
    PreloadImageComponent,
    TaricsSendEntitlementsComponent,
    MatBadgeModule,
    MatButtonModule,
    MatDialogModule,
    MatDividerModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslatePipe,
  ],
})
export class TopNavbarContentComponent extends BaseComponent implements OnInit {
  // @Input() messages = [];
  // @Input() notifications = [];
  sideMenuVisible = true;
  // userProfileVisible = true;

  notificationCount: NotificationCount;

  authUser: AuthUser;
  isAuthenticated = false;
  isTenantAdminAuthenticated = false;
  selectedOption: string;
  title: string;
  navbarSettings: TopNavbarSettings;
  helpSettings: HelpSettings;
  releasenotesSettings: ReleaseNotesSettings;
  termsSettings: TermsSettings;
  newsOpened = false;
  termsOpened = false;

  isErrorOccurred = false;
  isExceptionErrorFileLoading = false;

  isMenuDisabled = false;

  constructor(
    private messageNotifierService: MessageNotifierService,
    private sanitizer: DomSanitizer,
    private coreDataService: CoreDataService,
    private authService: AuthService,
    public dialog: MatDialog,
    private store: Store<RootStoreState.State>,
    private router: Router,
    private translate: TranslateService,
    private helpAuthService: HelpAuthService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscribe(this.store.pipe(select(AuthStoreSelectors.selectAuthUser)), (authUser) => {
      this.authUser = authUser;
      // if (this.authUser) {
      //   this.showWelcomMessage();
      // }
    });
    this.subscribe(
      this.initialData(),
      ([isJustAuth, isTenantAdminAuth, navbarSettings, helpSettings, termsSettings, releasenotesSettings]) => {
        this.isAuthenticated = isJustAuth;
        this.isTenantAdminAuthenticated = isTenantAdminAuth;
        this.navbarSettings = navbarSettings;
        this.helpSettings = helpSettings;
        if (this.isAuthenticated) {
          this.store.dispatch(NotificationsStoreAction.startPollingNotificationsCount());
        } else {
          this.store.dispatch(NotificationsStoreAction.finishPollingNotificationsCount());
        }
        if (
          this.isAuthenticated &&
          JSON.stringify(termsSettings) !== JSON.stringify(this.termsSettings) &&
          termsSettings != null &&
          termsSettings.termsConditions != null &&
          termsSettings.termsConditionsHereby != null
        ) {
          this.termsSettings = termsSettings;
          this.dialog.open(TermsModalComponent, {
            panelClass: 'terms-modal-dialog',
            width: '80vw',
            maxWidth: '80vw',
            height: '90vh',
            autoFocus: false,
            disableClose: true,
            hasBackdrop: true,
            data: {
              terms: this.termsSettings,
              tenantImage: this.navbarSettings.tenantImage,
            } as TermsModalData,
          });
        }
        if (
          (this.isAuthenticated || this.isTenantAdminAuthenticated) &&
          JSON.stringify(releasenotesSettings) !== JSON.stringify(this.releasenotesSettings) &&
          releasenotesSettings != null &&
          releasenotesSettings.settingReleaseNotesEnabled
        ) {
          this.releasenotesSettings = releasenotesSettings;
          if (
            releasenotesSettings?.settingReleaseNotes.length > 0 &&
            (this.termsSettings == null || this.termsSettings.termsConditions == null || this.termsSettings.termsConditionsHereby == null)
          )
            this.dialog
              .open(ReleaseNotesIntroModalComponent, {
                width: '56vw',
                maxWidth: '900px',
                autoFocus: false,
                disableClose: true,
              })
              .afterClosed()
              .subscribe((openReleaseNotes: boolean) => {
                if (!openReleaseNotes) {
                  return;
                }

                this.dialog.open(ReleaseNotesModalComponent, {
                  height: '80vh',
                  maxHeight: '80vh',
                  width: '80vw',
                  autoFocus: false,
                  disableClose: true,
                  data: {
                    releaseNotes: this.releasenotesSettings?.settingReleaseNotes,
                  } as ReleaseNotesModalData,
                });
              });
        }
      },
    );
    this.subscribe(this.store.select(ErrorHandlingStoreSelectors.selectIsErrorOccurred), (isError) => {
      this.isErrorOccurred = isError;
    });
    this.subscribe(this.store.select(NotificationsStoreSelectors.selectCountNotifications), (count) => {
      this.notificationCount = count;
      this.checkToastrMessages();
    });
    this.subscribe(
      this.store.pipe(select(CoreUiInterfaceStoreSelectors.selectCoreUiInterfaceIsUiDisabled)),
      (isDisabled) => (this.isMenuDisabled = isDisabled),
    );
  }

  private initialData() {
    return combineLatest([
      this.store.pipe(select(AuthStoreSelectors.selectIsAuthenticated)),
      this.store.pipe(select(AuthStoreSelectors.selectIsAuthenticatedTenantAdmin)),
      this.store.pipe(select(PreferencesSelectors.selectPreferencesTopNavBarSettings)),
      this.store.pipe(select(PreferencesSelectors.selectPreferencesHelp)),
      this.store.pipe(select(PreferencesSelectors.selectPreferencesTermsSettings)),
      this.store.pipe(select(PreferencesSelectors.selectPreferencesReleaseNotesSettings)),
    ]);
  }

  goToUsecases() {
    this.router.navigate(['', { outlets: { usecasesOutlet: 'usecases' } }], { skipLocationChange: environment.hideOutletUrl });
  }

  reload() {
    window.location.reload();
  }

  // Call this method in the image source, it will sanitize it.
  transform(base64Image: string) {
    return this.sanitizer.bypassSecurityTrustResourceUrl(base64Image);
  }

  goToNotifications() {
    this.router.navigate(['pages/notifications']);
  }

  goToIncomingMails() {
    this.router.navigate(['pages/inbox-mail']);
  }

  configurationOverlay() {
    this.router.navigate(['', { outlets: { configurationOutlet: 'configurations' } }], { skipLocationChange: environment.hideOutletUrl });
  }

  async helpOverlay() {
    let newWindow = window.open();
    document.body.classList.add('waiting-mouse-cursor');
    this.subscribe(
      this.helpAuthService.getHelpAuthToken(),
      (res) => {
        if (res.data && res.data.token) {
          newWindow.location.href = `${this.helpSettings.settingHelpUrl}?mo_jwt_token=${res.data.token}`;
          document.body.classList.remove('waiting-mouse-cursor');
        } else {
          newWindow.location.href = this.helpSettings.settingHelpUrl;
          document.body.classList.remove('waiting-mouse-cursor');
        }
      },
      (error) => {
        newWindow.location.href = this.helpSettings.settingHelpUrl;
        document.body.classList.remove('waiting-mouse-cursor');
      },
    );
  }

  logout() {
    this.store.dispatch(AuthStoreAction.logout());
  }

  loadExceptionErrorFile() {
    this.setErrorSpreadsheetLoading(true);
    const pr: PaginatedRequest = {
      pageIndex: 0,
      pageSize: 10,
      orderBy: 'error_timestamp',
      sort: 'desc',
    };
    this.subscribe(
      this.coreDataService.getExceptionErrorFileForLoggedUser(pr, this.isTenantAdminAuthenticated),
      (blob) => {
        this.setErrorSpreadsheetLoading(false);
        FileSaver.saveAs(blob.file, blob.filename); // FileSaver;
        this.store.dispatch(ErrorHandlingStoreAction.resetError());
      },
      (error) => this.setErrorSpreadsheetLoading(false),
    );
  }

  // toggleSideMenu(): void {
  //   this.sideMenuService.sidenav.toggle().then((this.sideMenuVisible = !this.sideMenuVisible));
  // }

  setErrorSpreadsheetLoading(isLoading: boolean) {
    this.isExceptionErrorFileLoading = isLoading;
    if (isLoading) {
      document.body.classList.add('waiting-mouse-cursor');
    } else {
      document.body.classList.remove('waiting-mouse-cursor');
    }
  }

  showWelcomMessage() {
    let welcomeMessage: string;
    const curHr = new Date().getHours();

    if (curHr >= 5 && curHr < 11) {
      welcomeMessage = _('dashboard_goodmorning');
    } else if (curHr >= 14 && curHr < 18) {
      welcomeMessage = _('dashboard_goodafternoon');
    } else if (curHr >= 18 && curHr < 22) {
      welcomeMessage = _('dashboard_goodevening');
    } else {
      welcomeMessage = _('dashboard_welcome');
    }
    this.subscribe(this.translate.get(welcomeMessage), (message) => {
      this.messageNotifierService.showInfoMessage(
        `${message} ${this.authUser.firstname ? this.authUser.firstname : this.authUser.fullname}`,
      );
    });
  }

  checkToastrMessages() {
    if (this.notificationCount.toastrMessages && this.notificationCount.toastrMessages.length > 0) {
      this.notificationCount.toastrMessages.forEach((msg) => {
        if (msg.id === NotificationKind.info) {
          this.messageNotifierService.showFixedSuccessMessage(msg.value);
        }
        if (msg.id === NotificationKind.warning) {
          this.messageNotifierService.showFixedWarningMessage(msg.value);
        }
        if (msg.id === NotificationKind.error) {
          this.messageNotifierService.showFixedErrorMessage(msg.value);
        }
      });
    }
  }

  viewAsUser() {
    this.dialog.open(ViewasuserModalDialogComponent, {
      panelClass: 'viewasuser-modal-dialog',
      width: '60vw',
      height: '80vh',
      autoFocus: false,
    });
  }

  contactUs() {
    window.location.href = `mailto:${this.navbarSettings.settingSupportEmailAddress}`;
  }

  get Environment() {
    return environment;
  }
}
